import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import { setSelectedClaim, setCustomer, setInsurances, setClaimEvents } from "../../application/actions";
import Step1PersonalInfo from "./Step1PersonalInfo";
import FormUnemployment from "./FormUnemployment";
import ApplicationConfirmation from './ApplicationConfirmation';
import { getCustomerData } from "../../application/api";
import FormGdpr from "./FormGdpr";
import { useAppState } from "../../application/AppState";
import useComposeApplication from "../../application/hooks/useComposeApplication";
import Loading from "../../components/Loading";
import useTranslation from "../../application/translation";
import { useTheme, useMediaQuery } from '@mui/material';
import { useRef, useEffect } from 'react';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    mainBox: {
        position: "relative",
        marginTop: "-8px",
        padding: "10px 20px",
        borderBottomRightRadius: "4px",
        borderBottomLeftRadius: "4px",
        background: theme.palette.background.default,
    },
}));

const StepContent = ({ step, ...rest }) => {
    switch (step) {
        case 0:
            return <Step1PersonalInfo {...rest} />;
        case 1:
            return <FormUnemployment {...rest} />;
        case 2:
            return <FormGdpr {...rest} />;
        default:
            return <p role="alert">Fel: Steg saknas</p>;
    }
};

export default function HorizontalLinearStepper() {
    const [{ selectedClaim }, dispatch] = useAppState();
    const classes = useStyles();
    const [activeStep, setActiveStep] = useState(0);
    const application = useComposeApplication();
    const [resultRef, setResultRef] = useState(null);
    const [busy, setBusy] = useState(false);
    const translation = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const contentRef = useRef(null);

    useEffect(() => {
        contentRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, [activeStep]);

    const steps = [
        translation.stepper_step1,
        translation.stepper_step2_unemployment,
        translation.stepper_step3
    ];

    const handleNext = () => {
        if (activeStep === 2) {
            postApplication();
            return;
        }
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        if (activeStep === 0) {
            dispatch(setSelectedClaim(null));
        }
        setActiveStep((prev) => prev - 1);
    };

    const postApplication = async () => {
        setBusy(true);
        const result = await application.send(selectedClaim);
        const cData = await getCustomerData();

        dispatch(setCustomer(cData.customer || {}));
        dispatch(setInsurances(cData.customerInsurances || []));
        dispatch(setClaimEvents(cData.claimEvents || []));
        setBusy(false);

        setResultRef(result ? result.reference : -1);
        dispatch({ type: "resetForms" });
    };

    if (busy) return <Loading label={translation.spinner_signing} />;
    if (resultRef) return <ApplicationConfirmation refNr={resultRef} />;

    return (
        <div className={classes.root}>
            <div ref={contentRef}>
                <Stepper
                    activeStep={activeStep}
                    aria-label={translation.stepper_header}
                    orientation={isMobile ? 'vertical' : 'horizontal'}
                >
                    {steps.map((label, index) => (
                        <Step key={index}>
                            <StepLabel
                                aria-current={activeStep === index ? "step" : undefined}
                                aria-label={`${translation.step} ${index + 1}: ${label}`}
                            >
                                {label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <StepContent
                    step={activeStep}
                    handleNext={handleNext}
                    handleBack={handleBack}
                />
            </div>
        </div>
    );
}
